import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CrearItemChecklist,
  ItemChecklist,
  PlantillaCompleta,
  PlantillaResumen,
  TipoChecklist,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { Icono } from '../componentes/Iconos.js';

interface Props {
  idioma: Idioma;
  onVolver: () => void;
}

const TIPOS: TipoChecklist[] = ['ENTRADA', 'SALIDA', 'PERIODICA', 'MANTENIMIENTO', 'OTRO'];

/**
 * Editor de plantillas de checklist, con la forma de la maqueta: la lista de
 * plantillas a la izquierda y, a la derecha, sus ítems agrupados por sección en
 * módulos plegables. Cada ítem lleva las marcas de crítico y de foto obligatoria.
 * Todo persiste en el backend real.
 */
export function Checklists({ idioma, onVolver }: Props) {
  const [plantillas, setPlantillas] = useState<PlantillaResumen[] | null>(null);
  const [elegida, setElegida] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<PlantillaCompleta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);

  const cargarLista = useCallback(() => {
    api
      .plantillas()
      .then((r) => {
        setPlantillas(r.plantillas);
        setElegida((actual) => actual ?? r.plantillas[0]?.id ?? null);
      })
      .catch(() => setError(t(idioma, 'error.servidor')));
  }, [idioma]);

  const cargarDetalle = useCallback(
    (id: string) => {
      api
        .plantilla(id)
        .then((r) => setDetalle(r.plantilla))
        .catch(() => setError(t(idioma, 'error.servidor')));
    },
    [idioma],
  );

  useEffect(() => {
    cargarLista();
  }, [cargarLista]);

  useEffect(() => {
    if (elegida) cargarDetalle(elegida);
    else setDetalle(null);
  }, [elegida, cargarDetalle]);

  const refrescar = () => {
    cargarLista();
    if (elegida) cargarDetalle(elegida);
  };

  const crearPlantilla = (nombre: string, tipo: TipoChecklist) => {
    api
      .crearPlantilla({ nombre, tipo })
      .then((r) => {
        setCreando(false);
        setElegida(r.plantilla.id);
        cargarLista();
      })
      .catch(() => setError(t(idioma, 'error.servidor')));
  };

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'checklists.titulo')}</h1>
          <p>{t(idioma, 'checklists.resumen', { total: plantillas?.length ?? 0 })}</p>
        </div>
        <div className="acciones">
          <button type="button" className="secundario" onClick={onVolver}>
            {t(idioma, 'app.volver')}
          </button>
          <button type="button" onClick={() => setCreando((v) => !v)}>
            + {t(idioma, 'checklists.nuevaPlantilla')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {creando && (
        <FormularioPlantilla
          idioma={idioma}
          onCrear={crearPlantilla}
          onCancelar={() => setCreando(false)}
        />
      )}

      {plantillas === null && !error && <p>{t(idioma, 'app.cargando')}</p>}

      {plantillas !== null && plantillas.length === 0 && !creando && (
        <div className="tarjeta">
          <p>{t(idioma, 'checklists.sinPlantillas')}</p>
        </div>
      )}

      {plantillas !== null && plantillas.length > 0 && (
        <div className="editor-plantillas">
          <div className="tarjeta plana" style={{ alignSelf: 'start' }}>
            <div className="lista">
              {plantillas.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="fila fila-pulsable"
                  aria-current={p.id === elegida ? 'true' : undefined}
                  onClick={() => setElegida(p.id)}
                >
                  <span className="crecer">
                    <b>{p.nombre}</b>
                    <small>
                      {t(idioma, `checklistTipo.${p.tipo}`)} · {p.totalItems}{' '}
                      {t(idioma, 'checklists.itemsCorto')}
                      {p.totalCriticos > 0 ? (
                        <>
                          {' · '}
                          <Icono nombre="cronometro" /> {p.totalCriticos}
                        </>
                      ) : (
                        ''
                      )}
                    </small>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {detalle ? (
            <DetallePlantilla
              key={detalle.id}
              idioma={idioma}
              plantilla={detalle}
              onCambio={refrescar}
              onError={() => setError(t(idioma, 'error.servidor'))}
            />
          ) : (
            <div className="tarjeta">
              <p>{t(idioma, 'checklists.elige')}</p>
            </div>
          )}
        </div>
      )}
    </main>
  );
}

function FormularioPlantilla({
  idioma,
  onCrear,
  onCancelar,
}: {
  idioma: Idioma;
  onCrear: (nombre: string, tipo: TipoChecklist) => void;
  onCancelar: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<TipoChecklist>('ENTRADA');

  return (
    <form
      className="tarjeta formulario"
      onSubmit={(e) => {
        e.preventDefault();
        if (nombre.trim().length >= 2) onCrear(nombre.trim(), tipo);
      }}
    >
      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor="pl-nombre">{t(idioma, 'checklists.nombre')}</label>
          <input
            id="pl-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            required
            minLength={2}
            maxLength={200}
          />
        </div>
        <div className="campo">
          <label htmlFor="pl-tipo">{t(idioma, 'checklists.tipo')}</label>
          <select id="pl-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoChecklist)}>
            {TIPOS.map((x) => (
              <option key={x} value={x}>
                {t(idioma, `checklistTipo.${x}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="fila-flex" style={{ gap: '0.5rem' }}>
        <button type="submit" disabled={nombre.trim().length < 2}>
          {t(idioma, 'checklists.guardar')}
        </button>
        <button type="button" className="secundario" onClick={onCancelar}>
          {t(idioma, 'checklists.cancelar')}
        </button>
      </div>
    </form>
  );
}

function DetallePlantilla({
  idioma,
  plantilla,
  onCambio,
  onError,
}: {
  idioma: Idioma;
  plantilla: PlantillaCompleta;
  onCambio: () => void;
  onError: () => void;
}) {
  const [nombre, setNombre] = useState(plantilla.nombre);
  const [anadiendo, setAnadiendo] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);

  const secciones = useMemo(() => {
    const orden: string[] = [];
    const mapa = new Map<string, ItemChecklist[]>();
    for (const it of plantilla.items) {
      if (!mapa.has(it.seccion)) {
        mapa.set(it.seccion, []);
        orden.push(it.seccion);
      }
      mapa.get(it.seccion)!.push(it);
    }
    return orden.map((s) => [s, mapa.get(s)!] as const);
  }, [plantilla.items]);

  const guardarNombre = () => {
    if (nombre.trim().length >= 2 && nombre.trim() !== plantilla.nombre) {
      api.editarPlantilla(plantilla.id, { nombre: nombre.trim() }).then(onCambio).catch(onError);
    }
  };

  const cambiarTipo = (tipo: TipoChecklist) => {
    api.editarPlantilla(plantilla.id, { tipo }).then(onCambio).catch(onError);
  };

  const archivar = () => {
    const accion = plantilla.activo
      ? api.archivarPlantilla(plantilla.id)
      : api.reactivarPlantilla(plantilla.id);
    accion.then(onCambio).catch(onError);
  };

  const borrarItem = (itemId: string) => {
    api.eliminarItemChecklist(itemId).then(onCambio).catch(onError);
  };

  const toggleMarca = (item: ItemChecklist, campo: 'critico' | 'exigeFoto') => {
    api
      .editarItemChecklist(item.id, { [campo]: !item[campo] })
      .then(onCambio)
      .catch(onError);
  };

  return (
    <div style={{ display: 'grid', gap: '0.9rem', minWidth: 0 }}>
      <div className="tarjeta">
        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="det-nombre">{t(idioma, 'checklists.nombre')}</label>
            <input
              id="det-nombre"
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              onBlur={guardarNombre}
              maxLength={200}
            />
          </div>
          <div className="campo">
            <label htmlFor="det-tipo">{t(idioma, 'checklists.tipo')}</label>
            <select
              id="det-tipo"
              value={plantilla.tipo}
              onChange={(e) => cambiarTipo(e.target.value as TipoChecklist)}
            >
              {TIPOS.map((x) => (
                <option key={x} value={x}>
                  {t(idioma, `checklistTipo.${x}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="fila-flex" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
          <span className="suave">
            {plantilla.totalItems} {t(idioma, 'checklists.itemsCorto')} · {t(idioma, 'checklists.criticos')}:{' '}
            <b>{plantilla.totalCriticos}</b>
          </span>
          {!plantilla.activo && (
            <span className="pastilla neutra">{t(idioma, 'checklists.archivada')}</span>
          )}
          <button
            type="button"
            className="enlace"
            style={{ marginLeft: 'auto' }}
            onClick={() => setAnadiendo((v) => !v)}
          >
            + {t(idioma, 'checklists.anadirItem')}
          </button>
          <button type="button" className="enlace" onClick={archivar}>
            {plantilla.activo ? t(idioma, 'checklists.archivar') : t(idioma, 'checklists.reactivar')}
          </button>
        </div>

        {anadiendo && (
          <FormularioItem
            idioma={idioma}
            seccionesExistentes={secciones.map(([s]) => s)}
            onCrear={(datos) => {
              api
                .crearItemChecklist(plantilla.id, datos)
                .then(() => {
                  setAnadiendo(false);
                  onCambio();
                })
                .catch(onError);
            }}
            onCancelar={() => setAnadiendo(false)}
          />
        )}
      </div>

      {secciones.length === 0 && (
        <div className="tarjeta">
          <p className="suave">{t(idioma, 'checklists.sinItems')}</p>
        </div>
      )}

      {secciones.map(([seccion, items]) => {
        const criticos = items.filter((i) => i.critico).length;
        return (
          <details key={seccion} className="plegable" open>
            <summary>
              {seccion.toUpperCase()}{' '}
              <span className="chip-area">
                {items.length} {t(idioma, 'checklists.itemsCorto')}
              </span>
              {criticos > 0 && (
                <span className="chip-sla">
                  <Icono nombre="cronometro" /> {criticos}
                </span>
              )}
            </summary>
            <div className="cuerpo">
              <ol className="items-plantilla">
                {items.map((item) => (
                  <li key={item.id}>
                    {editando === item.id ? (
                      <FormularioItem
                        idioma={idioma}
                        seccionesExistentes={secciones.map(([s]) => s)}
                        item={item}
                        onCrear={(datos) => {
                          api
                            .editarItemChecklist(item.id, datos)
                            .then(() => {
                              setEditando(null);
                              onCambio();
                            })
                            .catch(onError);
                        }}
                        onCancelar={() => setEditando(null)}
                      />
                    ) : (
                      <>
                        <span className="texto-item">
                          <b>{item.texto}</b>
                        </span>
                        <span className="marcas">
                          <button
                            type="button"
                            className={item.critico ? 'chip-sla' : 'chip-area'}
                            onClick={() => toggleMarca(item, 'critico')}
                            title={t(idioma, 'checklists.critico')}
                          >
                            <Icono nombre="cronometro" /> {t(idioma, 'checklists.critico')}
                          </button>
                          <button
                            type="button"
                            className={item.exigeFoto ? 'chip-foto' : 'chip-area'}
                            onClick={() => toggleMarca(item, 'exigeFoto')}
                            title={t(idioma, 'checklists.foto')}
                          >
                            <Icono nombre="camara" /> {t(idioma, 'checklists.foto')}
                          </button>
                        </span>
                        <span className="acciones-item">
                          <button
                            type="button"
                            className="enlace"
                            aria-label={t(idioma, 'checklists.editar')}
                            onClick={() => setEditando(item.id)}
                          >
                            <Icono nombre="lapiz" />
                          </button>
                          <button
                            type="button"
                            className="enlace"
                            aria-label={t(idioma, 'checklists.eliminar')}
                            onClick={() => borrarItem(item.id)}
                          >
                            <Icono nombre="papelera" />
                          </button>
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </details>
        );
      })}
    </div>
  );
}

function FormularioItem({
  idioma,
  seccionesExistentes,
  item,
  onCrear,
  onCancelar,
}: {
  idioma: Idioma;
  seccionesExistentes: string[];
  item?: ItemChecklist;
  onCrear: (datos: CrearItemChecklist) => void;
  onCancelar: () => void;
}) {
  const [seccion, setSeccion] = useState(item?.seccion ?? seccionesExistentes[0] ?? 'GENERAL');
  const [texto, setTexto] = useState(item?.texto ?? '');
  const [critico, setCritico] = useState(item?.critico ?? false);
  const [exigeFoto, setExigeFoto] = useState(item?.exigeFoto ?? false);

  return (
    <form
      className="formulario"
      style={{ marginTop: '0.5rem' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (texto.trim().length >= 2 && seccion.trim().length >= 1) {
          onCrear({ seccion: seccion.trim(), texto: texto.trim(), critico, exigeFoto });
        }
      }}
    >
      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor="it-texto">{t(idioma, 'checklists.texto')}</label>
          <input
            id="it-texto"
            type="text"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            required
            minLength={2}
            maxLength={400}
          />
        </div>
        <div className="campo">
          <label htmlFor="it-seccion">{t(idioma, 'checklists.seccion')}</label>
          <input
            id="it-seccion"
            type="text"
            value={seccion}
            onChange={(e) => setSeccion(e.target.value)}
            list="secciones-existentes"
            maxLength={80}
          />
          <datalist id="secciones-existentes">
            {seccionesExistentes.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
      </div>
      <div className="fila-flex" style={{ gap: '1rem', flexWrap: 'wrap' }}>
        <label className="conmutador-check">
          <input type="checkbox" checked={critico} onChange={(e) => setCritico(e.target.checked)} />{' '}
          {t(idioma, 'checklists.critico')}
        </label>
        <label className="conmutador-check">
          <input
            type="checkbox"
            checked={exigeFoto}
            onChange={(e) => setExigeFoto(e.target.checked)}
          />{' '}
          {t(idioma, 'checklists.foto')}
        </label>
      </div>
      <div className="fila-flex" style={{ gap: '0.5rem' }}>
        <button type="submit" disabled={texto.trim().length < 2}>
          {t(idioma, 'checklists.guardar')}
        </button>
        <button type="button" className="secundario" onClick={onCancelar}>
          {t(idioma, 'checklists.cancelar')}
        </button>
      </div>
    </form>
  );
}
